import { ReactElement, useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FaChevronRight, FaPhotoVideo, FaVideo } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import videoTemplate from "../assets/upload-templ.png";
import Button from "../components/Button";
import Page from "../components/Page";

import { Camera } from "../components/Camera";
import Menu from "../components/Menu";
import { CreatePost } from "../types";

import { db } from "@/firebase";
import { useGetPlace } from "@/hooks/useGetPlace";
import { getAuth } from "firebase/auth";
import { addDoc, collection } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import Star from "../assets/Star";
import useCreatePost from "../hooks/useCreatePost";
import validateVideo from "@/common/validateVideo";
import { captureException } from "@sentry/react";
import {
  getPartnerConfig,
  getSuccessRoute,
  getVideoTimeLimits,
} from "@/common/partnerConfig";
import { trackEvent } from "@/lib/amplitude";

function UploadVideo() {
  const { t } = useTranslation();

  const placeRef = window.localStorage.getItem("placeRef");
  const partnerConfig = getPartnerConfig(placeRef);
  const timeLimits = getVideoTimeLimits(placeRef);
  const hasTrackedPageView = useRef(false);
  const lastProgressMilestone = useRef(0);

  // Track page view
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackEvent("upload_page_viewed", {
        placeRef,
        partner: partnerConfig?.id,
      });
    }
  }, []);

  const { mutate, isPending } = useCreatePost({
    onSuccess: () => {
      trackEvent("video_upload_success", {
        placeRef,
        partner: partnerConfig?.id,
      });
      navigate(getSuccessRoute(placeRef), {
        state: { originalVideo: video },
      });
    },
    onError: async (error) => {
      console.error(error);
      trackEvent("video_upload_error", {
        placeRef,
        partner: partnerConfig?.id,
        errorName: error.name,
        errorMessage: error.message,
      });
      await addDoc(collection(db, "errors"), {
        step: "useCreatePost",
        name: error.name,
        message: error.message,
        uid: getAuth().currentUser?.uid,
        app: "web-qr",
        placeRef: window.localStorage.getItem("placeRef"),
        stack: error.stack || "",
        createdAt: new Date(),
      });
      if (error.message === "Place Ref not present") {
        alert(t("upload-video.alert-error") + "🙏");
      } else alert(t("upload-video.alert-error"));
    },
  });

  const navigate = useNavigate();
  const ref = useRef<HTMLInputElement>(null);
  const { isPending: placePending, isError, error } = useGetPlace(53467);
  const [openCam, setOpenCam] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const {
    handleSubmit,
    setValue,
    resetField,
    control,
    formState: { errors },
    register,
    watch,
  } = useForm<CreatePost>({
    defaultValues: {
      title: "qr-code-review",
      caption: "live",
      rating: 0,
      video: undefined,
    },
  });

  const video = watch("video");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (video instanceof File) {
      const url = URL.createObjectURL(video);
      setVideoUrl(url);
      return () => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // ignore
        }
      };
    }
    setVideoUrl(null);
  }, [video]);

  const onVideoChange = async (video: File, source: "record" | "gallery") => {
    try {
      await validateVideo(video, placeRef);

      trackEvent(
        source === "record" ? "video_record_completed" : "video_selected_from_gallery",
        {
          placeRef,
          partner: partnerConfig?.id,
          videoSize: video.size,
          videoType: video.type,
        },
      );

      setValue("video", video);
    } catch (message) {
      captureException(new Error(`Video validation failed: ${message}`));
      onVideoRemoved();
      alert(message);
    }
  };

  const onVideoRemoved = () => {
    resetField("video");
    if (ref.current) ref.current.value = "";
  };

  const onSubmitReview: SubmitHandler<CreatePost> = (data) => {
    if (!data.rating) {
      alert(t("upload-video.alert-missing-rating"));
      return;
    }

    if (!data.video || (data.video && !video)) {
      alert(t("upload-video.alert-missing-video"));
      return;
    }

    trackEvent("review_submitted", {
      placeRef,
      partner: partnerConfig?.id,
      rating: data.rating,
    });

    // pass an onProgress callback to update UI and ensure progress is cleared when done
    mutate(
      {
        ...data,
        onProgress: (p: number) => {
          setUploadProgress(p);
          // Track progress milestones (25%, 50%, 75%, 100%)
          const milestone = Math.floor(p / 25) * 25;
          if (milestone > lastProgressMilestone.current && milestone > 0) {
            lastProgressMilestone.current = milestone;
            trackEvent("video_upload_progress", {
              placeRef,
              partner: partnerConfig?.id,
              progress: milestone,
            });
          }
        },
      },
      {
        onSettled: () => {
          setUploadProgress(null);
          lastProgressMilestone.current = 0;
        },
      },
    );
  };

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!placePending && isError) {
      navigate("/tag-place");
    }
  }, [placePending]);

  const renderStars = (
    value: number,
    onChange: (value: number) => void,
  ): ReactElement => {
    return (
      <>
        {[...Array(5)].map((_, index) => (
          <Star
            key={index}
            className={`cursor-pointer ${
              index < value ? "text-primary" : "text-accent opacity-50"
            }`}
            onClick={() => {
              const rating = index + 1;
              onChange(rating);
              trackEvent("rating_selected", {
                placeRef,
                partner: partnerConfig?.id,
                rating,
              });
            }}
          />
        ))}
      </>
    );
  };

  return (
    <>
      {openCam && (
        <Camera
          onClose={() => setOpenCam(false)}
          onChange={(file) => onVideoChange(file, "record")}
          maxRecordTime={timeLimits.max}
          translationSuffix={partnerConfig?.guidelines.customTranslationSuffix}
        />
      )}

      {isPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" aria-hidden>
          {/* prevent interaction with the page while upload is pending */}
        </div>
      )}

      <Page
        title={t("upload-video.title")}
        back
        action={
          <Button
            onPress={() => {
              handleSubmit(onSubmitReview)();
            }}
            form="upload-form"
            isDisabled={isPending}
            isPending={isPending}
            uploadPercent={uploadProgress}
          >
            {isPending ? t("upload-video.uploading") : t("upload-video.upload-button")}
          </Button>
        }
      >
        <form id="upload-form" ref={formRef}>
          <input
            {...register("video")}
            onChange={(e) => {
              if (e?.target?.files?.[0]) {
                onVideoChange(e?.target?.files?.[0], "gallery");
              }
            }}
            hidden
            ref={ref}
            type="file"
            accept="video/*"
          />
          <div className="flex flex-col">
            <div className="flex items-center justify-center py-4">
              {video ? (
                <div className="relative flex w-full flex-col items-center justify-center space-y-4  rounded-2xl">
                  <video
                    className="max-h-[40vh] max-w-[90vw]"
                    height="100%"
                    src={videoUrl || undefined}
                    autoPlay
                    playsInline
                    controls
                  />

                  <button onClick={onVideoRemoved} aria-label="clear video">
                    <span className="text-xl underline">
                      {t("upload-video.clear-video")}
                    </span>
                  </button>
                </div>
              ) : (
                <Menu
                  trigger={
                    <div className="relative flex w-48 items-center justify-center rounded-2xl">
                      <img src={videoTemplate} alt="Video template" />
                      <span className="absolute text-4xl">+</span>
                    </div>
                  }
                  items={[
                    {
                      id: "video",
                      action: () => {
                        trackEvent("video_record_started", {
                          placeRef,
                          partner: partnerConfig?.id,
                        });
                        setOpenCam(true);
                      },
                      element: (
                        <div className="flex items-center space-x-2">
                          <FaVideo />
                          <span>{t("upload-video.select.record")}</span>
                        </div>
                      ),
                    },
                    {
                      id: "gallery",
                      action: () => ref.current?.click(),
                      element: (
                        <div className="flex items-center space-x-2">
                          <FaPhotoVideo />
                          <span>{t("upload-video.select.gallery")}</span>
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </div>

            <div className="pt-2">
              <div className="flex flex-col items-start pt-5">
                <span className="font-bold">{t("upload-video.inputs.rating.title")}</span>

                <div className="flex w-full  items-center justify-center space-x-4 p-4 px-14 ">
                  <Controller
                    control={control}
                    name="rating"
                    rules={{ required: true }}
                    render={({ field }) => renderStars(field.value, field.onChange)}
                  />
                </div>
              </div>

              <Link
                to="/guidelines"
                className="flex w-full items-center justify-between py-6 "
              >
                <span>{t("upload-video.inputs.guidelines")}</span>
                <FaChevronRight />
              </Link>
            </div>
          </div>
        </form>
      </Page>
    </>
  );
}

export default UploadVideo;
